// frontend/src/components/ui/Input.tsx
import React, { forwardRef } from 'react';
import { cn } from '@/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  variant?: 'default' | 'filled' | 'flushed';
  inputSize?: 'sm' | 'md' | 'lg';
  rounded?: 'sm' | 'md' | 'lg' | 'full';
  responsive?: boolean; // Automatically adjust size based on screen size
  fullWidth?: boolean;
  labelPosition?: 'top' | 'left' | 'floating';
}

const inputVariants = {
  default: 'border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600',
  filled: 'border-transparent bg-gray-100 dark:bg-gray-700',
  flushed: 'border-0 border-b border-gray-300 rounded-none bg-transparent dark:border-gray-600',
};

const sizeVariants = {
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-3 py-2 text-sm min-h-[40px]',
  lg: 'px-4 py-3 text-base min-h-[48px]',
};

// Responsive size variants for mobile-friendly touch targets
const responsiveSizeVariants = {
  sm: 'px-3 py-2 text-sm sm:py-1.5 min-h-[40px] sm:min-h-[32px]',
  md: 'px-3 py-2.5 text-sm sm:py-2 min-h-[44px] sm:min-h-[40px]',
  lg: 'px-4 py-3.5 text-base sm:py-3 min-h-[52px] sm:min-h-[48px]',
};

const roundedVariants = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  onRightIconClick,
  variant = 'default',
  inputSize = 'md',
  rounded = 'md',
  responsive = false,
  fullWidth = true,
  labelPosition = 'top',
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const currentSizeVariants = responsive ? responsiveSizeVariants : sizeVariants;

  // Floating label logic
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(Boolean(props.value || props.defaultValue));
  const isFloating = labelPosition === 'floating' && (isFocused || hasValue);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(Boolean(e.target.value));
    props.onChange?.(e);
  };

  const inputElement = (
    <div className="relative">
      {leftIcon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-400 text-sm sm:text-sm">{leftIcon}</span>
        </div>
      )}
      
      <input
        ref={ref}
        id={inputId}
        className={cn(
          'block shadow-sm appearance-none',
          'focus:border-primary-500 focus:ring-primary-500',
          'dark:text-white dark:placeholder-gray-400',
          'dark:focus:border-primary-500 dark:focus:ring-primary-500',
          'transition-all duration-200',
          // Mobile-specific improvements
          'touch-manipulation',
          // Prevent zoom on iOS
          'text-base sm:text-sm',
          inputVariants[variant],
          currentSizeVariants[inputSize],
          variant !== 'flushed' && roundedVariants[rounded],
          leftIcon && 'pl-9 sm:pl-10',
          rightIcon && 'pr-9 sm:pr-10',
          error && 'border-red-300 focus:border-red-500 focus:ring-red-500',
          props.disabled && 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700',
          fullWidth ? 'w-full' : '',
          // Floating label adjustments
          labelPosition === 'floating' && 'pt-6 pb-2',
          className
        )}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        {...props}
      />
      
      {/* Floating Label */}
      {labelPosition === 'floating' && label && (
        <label
          htmlFor={inputId}
          className={cn(
            'absolute left-3 transition-all duration-200 pointer-events-none',
            'text-gray-500 dark:text-gray-400',
            isFloating
              ? 'top-1.5 text-xs font-medium'
              : 'top-1/2 -translate-y-1/2 text-sm'
          )}
        >
          {label}
        </label>
      )}
      
      {rightIcon && (
        <div 
          className={cn(
            "absolute inset-y-0 right-0 pr-3 flex items-center",
            onRightIconClick ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-r-md" : "pointer-events-none"
          )}
          onClick={onRightIconClick}
        >
          <span className="text-gray-400 text-sm sm:text-sm">{rightIcon}</span>
        </div>
      )}
    </div>
  );

  if (labelPosition === 'left') {
    return (
      <div className="space-y-1">
        <div className="flex items-center space-x-3">
          {label && (
            <label
              htmlFor={inputId}
              className="flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-0"
            >
              {label}
            </label>
          )}
          <div className="flex-1 min-w-0">
            {inputElement}
          </div>
        </div>
        
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 pl-0 sm:pl-24" role="alert">{error}</p>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-gray-500 dark:text-gray-400 pl-0 sm:pl-24">{helperText}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {labelPosition === 'top' && label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}
      
      {inputElement}
      
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';